<script>
  import { Icon, ArrowUp, XMark, Cog6Tooth } from "svelte-hero-icons";
  import { ai } from "../store";
  import { onMount } from "svelte";
  import AnimatedLogo from "./AnimatedLogo.svelte";

  export let isOpen = false;

  let promptInput = "";
  let textareaElement;
  let selectedModel = "claude-4-sonnet";

  const aiModels = [
    { id: "claude-4-sonnet", name: "Claude 4 Sonnet", context: "200k", output: "-" },
    { id: "claude-4-sonnet-1m", name: "Claude 4 Sonnet 1M", context: "-", output: "1M" },
    { id: "claude-4.1-opus", name: "Claude 4.1 Opus", context: "-", output: "200k" },
    { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash", context: "-", output: "1M" },
    { id: "gemini-2.5-pro", name: "Gemini 2.5 Pro", context: "200k", output: "1M" },
    { id: "gpt-4.1", name: "GPT 4.1", context: "200k", output: "1M" },
    { id: "gpt-5", name: "GPT-5", context: "272k", output: "-" },
    { id: "gpt-5-fast", name: "GPT-5 Fast", context: "272k", output: "-" },
    { id: "grok-code", name: "Grok Code", context: "256k", output: "-" },
  ];

  $: if (isOpen && textareaElement) {
    // Focus the textarea when overlay opens
    setTimeout(() => textareaElement.focus(), 100);
  }

  function handleSubmit() {
    if (promptInput.trim()) {
      ai.sendPrompt(promptInput.trim());
      promptInput = "";
    }
  }

  function handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
    if (event.key === "Escape") {
      ai.closeOverlay();
    }
  }

  function handleOverlayClick(event) {
    if (event.target === event.currentTarget) {
      ai.closeOverlay();
    }
  }

  onMount(() => {
    const handleKeydownGlobal = (event) => {
      if (event.key === "Escape" && isOpen) {
        ai.closeOverlay();
      }
    };

    document.addEventListener("keydown", handleKeydownGlobal);
    return () => document.removeEventListener("keydown", handleKeydownGlobal);
  });
</script>

{#if isOpen}
  <!-- Overlay backdrop -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="fixed inset-0 bg-black/10 flex items-center justify-center z-50"
    on:click={handleOverlayClick}
  >
    <!-- Modal content -->
    <div class="bg-surface rounded-lg shadow-xl w-full max-w-2xl mx-4">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-line-strong">
        <div class="flex items-center space-x-2">
          <Icon src={Cog6Tooth} class="w-6 h-6 text-brand" />
          <h2 class="text-xl font-semibold text-content">AI Assistant</h2>
        </div>
        <button
          on:click={() => ai.closeOverlay()}
          class="text-content-subtle hover:text-content-muted transition-colors"
        >
          <Icon src={XMark} class="w-6 h-6" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <div class="flex items-start space-x-8">
          <!-- AI Animation -->
          <div class="relative flex-shrink-0 mt-6">
            <div
              class="w-16 h-16 border-2 border-brand rounded-full flex items-center justify-center"
            >
              <AnimatedLogo size="w-10 h-10" />
            </div>
          </div>

          <!-- Prompt input area with embedded send button -->
          <div class="flex-1">
            <div class="relative">
              <textarea
                id="ai-prompt"
                bind:this={textareaElement}
                bind:value={promptInput}
                on:keydown={handleKeydown}
                placeholder="Ask me anything about your automation workflow, debugging, or testing strategies..."
                class="w-full p-4 pr-12 border border-line-strong rounded-xl resize-none focus:ring-2 focus:ring-brand focus:border-brand transition-colors text-base"
                rows="3"
              ></textarea>

              <!-- Send button inside textarea -->
              <button
                on:click={handleSubmit}
                disabled={!promptInput.trim()}
                class="absolute right-2 bottom-2 w-8 h-8 bg-brand hover:bg-brand/90 disabled:bg-surface-hover disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors"
                title="Send prompt (Enter to send, Shift+Enter for new line, Esc to close)"
              >
                <Icon src={ArrowUp} class="w-4 h-4 text-white" />
              </button>
            </div>

            <!-- AI Model Selector -->
            <div class="mt-3">
              <select
                id="ai-model"
                bind:value={selectedModel}
                class="w-full text-xs border border-line-strong rounded-md px-2 py-1 focus:ring-1 focus:ring-brand focus:border-brand transition-colors bg-surface-sunken text-content-muted"
              >
                {#each aiModels as model}
                  <option value={model.id}>
                    {model.name} - {model.context} / {model.output}
                  </option>
                {/each}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}
